/**
 * SieMultiFileRow Component
 * 
 * Specialized file row for GL_SIE files that supports multi-file upload.
 * Allows users to stage and merge up to 50 SIE files (e.g., one per voucher series).
 * 
 * Uses useInvalidateSieMasterData to trigger automatic refetch across all
 * components using SIE data after successful uploads.
 */

import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  FileSpreadsheet, 
  Upload, 
  CheckCircle, 
  Loader2, 
  HelpCircle,
  Files,
  Info,
  AlertTriangle,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useSieMasterData, useInvalidateSieMasterData } from '@/hooks/useSieMasterData';
import { supabase } from '@/integrations/supabase/client';
import { SieFileStagingView, type StagedFile } from '@/components/sie/SieFileStagingView';
import { decodeSieFile, validateSieContent, isSieFile } from '@/lib/sie-upload-utils';
import { getFileKeyConfig, getFileKeyDisplayName } from '@/lib/modules/file-keys';
import type { FileKey } from '@/lib/modules/types';

interface SieMultiFileRowProps {
  fileKey: FileKey;
  required: boolean;
  auditId: string;
  moduleId: string;
  onUploadComplete: () => void;
  /** Optional label override for display name */
  label?: string;
  /** Fiscal year index: 0 = current year, -1 = previous year. Defaults to 0. */
  fiscalYearIndex?: 0 | -1;
}

export function SieMultiFileRow({ 
  fileKey, 
  required, 
  auditId, 
  moduleId, 
  onUploadComplete,
  label,
  fiscalYearIndex = 0,
}: SieMultiFileRowProps) {
  const { toast } = useToast();
  const { data: sieData, isLoading: isLoadingSieData } = useSieMasterData(auditId);
  const invalidateSieMasterData = useInvalidateSieMasterData();
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showStaging, setShowStaging] = useState(false);
  const [mergeResult, setMergeResult] = useState<{
    totalFiles: number;
    totalVouchers: number;
    duplicatesSkipped: number;
  } | null>(null);
  
  const config = getFileKeyConfig(fileKey);
  const baseDisplayName = getFileKeyDisplayName(fileKey);
  const displayName = label || baseDisplayName;
  const MAX_FILES = 50;
  const yearLabel = fiscalYearIndex === 0 ? 'innevarande år' : 'föregående år';

  // Get existing SIE data for this fiscal year from shared cache
  const existingSieData = fiscalYearIndex === 0 
    ? sieData?.currentYear 
    : sieData?.previousYear;
  
  const hasExistingData = !!existingSieData;
  const companyName = existingSieData?.metadata?.companyName || '';
  const voucherCount = existingSieData?.aggregated?.statistics?.totalVouchers || 0;
  
  // Get merge metadata (only available for current year from useSieMasterData currently)
  const mergeMetadataFromDb = fiscalYearIndex === 0 ? sieData?.mergeMetadata : null;
  const displayMergeInfo = mergeResult || mergeMetadataFromDb;

  // Handle file selection
  const handleFilesSelected = useCallback((files: File[]) => {
    const validFiles: StagedFile[] = [];
    const invalidFiles: string[] = [];
    
    for (const file of files) {
      if (isSieFile(file)) {
        validFiles.push({
          file,
          id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          status: 'pending',
          progress: 0,
        });
      } else {
        invalidFiles.push(file.name);
      }
    }
    
    if (invalidFiles.length > 0) {
      toast({
        title: 'Ogiltiga filer ignorerades',
        description: `${invalidFiles.length} fil(er) hade inte giltigt SIE-format`,
        variant: 'destructive',
      });
    }
    
    if (validFiles.length > 0) {
      setStagedFiles(prev => {
        const combined = [...prev, ...validFiles];
        if (combined.length > MAX_FILES) {
          toast({
            title: 'Max antal filer',
            description: `Begränsat till ${MAX_FILES} filer. ${combined.length - MAX_FILES} filer ignorerades.`,
          });
          return combined.slice(0, MAX_FILES);
        }
        return combined;
      });
      setShowStaging(true);
    }
  }, [toast]);

  // Remove a staged file
  const handleRemoveFile = useCallback((id: string) => {
    setStagedFiles(prev => {
      const updated = prev.filter(f => f.id !== id);
      if (updated.length === 0) {
        setShowStaging(false);
      }
      return updated;
    });
  }, []);

  // Clear all staged files
  const handleClearAll = useCallback(() => {
    setStagedFiles([]);
    setShowStaging(false);
  }, []);

  // Add more files
  const handleAddMore = useCallback(() => {
    // Trigger the file input
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.se,.si,.sie,.sie4,text/plain';
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files) {
        handleFilesSelected(Array.from(target.files));
      }
    };
    input.click();
  }, [handleFilesSelected]);

  // Process all staged files
  const processMultipleFiles = useCallback(async () => {
    if (stagedFiles.length === 0) return;
    
    setIsProcessing(true);
    
    try {
      // Update all files to processing state
      setStagedFiles(prev => prev.map(f => ({ ...f, status: 'processing' as const, progress: 0 })));
      
      // Read and validate all file contents
      const fileContents: { filename: string; content: string }[] = [];
      
      for (let i = 0; i < stagedFiles.length; i++) {
        const stagedFile = stagedFiles[i];
        
        setStagedFiles(prev => prev.map(f => 
          f.id === stagedFile.id ? { ...f, progress: 30 } : f
        ));
        
        const content = await decodeSieFile(stagedFile.file);
        const validation = validateSieContent(content);
        
        if (!validation.valid) {
          setStagedFiles(prev => prev.map(f => 
            f.id === stagedFile.id ? { ...f, status: 'error' as const, error: validation.error } : f
          ));
          toast({
            title: `Ogiltig fil: ${stagedFile.file.name}`,
            description: validation.error,
            variant: 'destructive',
          });
          continue;
        }
        
        fileContents.push({
          filename: stagedFile.file.name,
          content,
        });
        
        setStagedFiles(prev => prev.map(f => 
          f.id === stagedFile.id ? { ...f, progress: 80 } : f
        ));
      }
      
      if (fileContents.length === 0) {
        throw new Error('Inga giltiga SIE-filer att bearbeta');
      }
      
      console.log(`[SieMultiFileRow] Sending ${fileContents.length} files to batch processor`);
      
      // Call batch processing edge function
      const { data, error } = await supabase.functions.invoke('process-sie-files-batch', {
        body: {
          auditId,
          sieContents: fileContents,
          saveToDatabase: true,
          fiscalYearIndex,
        },
      });
      
      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error || 'Okänt fel vid bearbetning');
      
      // Mark all valid files as done
      setStagedFiles(prev => prev.map(f => 
        f.status !== 'error' ? { ...f, status: 'done' as const, progress: 100 } : f
      ));
      
      // Store merge result info
      setMergeResult({
        totalFiles: fileContents.length,
        totalVouchers: data.data?.aggregated?.statistics?.totalVouchers || 0,
        duplicatesSkipped: data.mergeWarnings?.filter((w: { type: string }) => w.type === 'duplicate_voucher')?.length || 0,
      });
      
      toast({
        title: `SIE-filer sammanslagna (${yearLabel})`,
        description: `${fileContents.length} filer bearbetades. ${data.data?.aggregated?.statistics?.totalVouchers || 0} verifikationer totalt.`,
      });
      
      // Trigger callback and invalidate shared cache for all SIE components
      onUploadComplete();
      invalidateSieMasterData(auditId);
      
    } catch (error) {
      console.error('[SieMultiFileRow] Error:', error);
      toast({
        title: 'Fel vid bearbetning',
        description: error instanceof Error ? error.message : 'Kunde inte bearbeta SIE-filerna',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  }, [stagedFiles, auditId, toast, onUploadComplete]);

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFilesSelected,
    accept: {
      'text/plain': ['.se', '.si', '.sie', '.sie4'],
    },
    multiple: true,
    maxFiles: MAX_FILES,
    disabled: isProcessing,
  });

  // Show loading state while fetching SIE data
  if (isLoadingSieData) {
    return (
      <div className="border rounded-lg p-4 bg-card animate-pulse">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Laddar SIE-data...</span>
        </div>
      </div>
    );
  }

  // If we have existing data and not staging new ones, show status
  if (hasExistingData && !showStaging) {
    return (
      <div className="border rounded-lg p-4 bg-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{displayName}</span>
                {required && <Badge variant="default" className="text-xs">Obligatorisk</Badge>}
                <Badge variant="secondary" className="text-xs">
                  <Files className="h-3 w-3 mr-1" />
                  Multi-fil
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {companyName} • {voucherCount} verifikationer
                {displayMergeInfo && (
                  <span className="ml-2">
                    ({displayMergeInfo.totalFiles} filer sammanslagna)
                  </span>
                )}
              </p>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowStaging(true)}
            disabled={isProcessing}
          >
            <Upload className="h-4 w-4 mr-2" />
            Ladda upp nya
          </Button>
        </div>
      </div>
    );
  }

  // Staging view with files
  if (showStaging && stagedFiles.length > 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium">{displayName}</span>
            {required && <Badge variant="default" className="text-xs">Obligatorisk</Badge>}
            <Badge variant="secondary" className="text-xs">
              <Files className="h-3 w-3 mr-1" />
              Multi-fil stöd
            </Badge>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px]">
                <p>Du kan ladda upp flera SIE-filer (t.ex. en per verifikatserie) 
                   som slås samman automatiskt. Max {MAX_FILES} filer.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        <SieFileStagingView
          files={stagedFiles}
          onRemoveFile={handleRemoveFile}
          onClearAll={handleClearAll}
          onAddMore={handleAddMore}
          onProcess={processMultipleFiles}
          isProcessing={isProcessing}
          maxFiles={MAX_FILES}
        />
      </div>
    );
  }

  // Empty state - dropzone
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium">{displayName}</span>
          {required && <Badge variant="default" className="text-xs">Obligatorisk</Badge>}
          <Badge variant="secondary" className="text-xs">
            <Files className="h-3 w-3 mr-1" />
            Multi-fil stöd
          </Badge>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[300px]">
              <p>{config.descriptionKey}</p>
              <p className="mt-1 text-xs opacity-80">
                Stöder upp till {MAX_FILES} SIE-filer som slås samman automatiskt.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
          transition-colors duration-200
          ${isDragActive 
            ? 'border-primary bg-primary/5' 
            : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
          }
        `}
      >
        <input {...getInputProps()} />
        <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm font-medium">
          {isDragActive ? 'Släpp filerna här' : 'Dra och släpp SIE-filer här'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          eller klicka för att välja • upp till {MAX_FILES} filer
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Accepterar: {config.formats.join(', ')}
        </p>
      </div>
      
      <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-sm text-blue-800 dark:text-blue-200">
          Flera SIE-filer (t.ex. en per verifikatserie) slås samman automatiskt. 
          Dubbletter hanteras och varningar visas vid konflikter.
        </AlertDescription>
      </Alert>
    </div>
  );
}
